/**
 * Crypto Buffer Hooks
 * TanStack Query hooks for per-crypto quote buffers
 */

import {
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import {
    createBufferTier,
    deleteBufferTier,
    getCryptoBufferConfig,
    getCryptocurrencies,
    patchCryptoBuffer,
} from '@/services/crypto-buffer.service'
import type {
    CreateBufferTierRequest,
    CryptoBufferConfig,
    CryptoSummary,
    PatchCryptoBufferRequest,
} from '@/types/crypto-buffer'

const KEY = 'crypto-buffers'

export const useCryptocurrencies = () =>
    useQuery<CryptoSummary[]>({
        queryKey: [KEY, 'list'],
        queryFn: getCryptocurrencies,
        staleTime: 1000 * 60 * 2,
    })

/** Buffer config for every listed crypto, so the list can show which are unconfigured. */
export const useCryptoBufferConfigs = (cryptoIds: string[]) =>
    useQueries({
        queries: cryptoIds.map(id => ({
            queryKey: [KEY, 'detail', id],
            queryFn: () => getCryptoBufferConfig(id),
            staleTime: 1000 * 60 * 2,
        })),
        combine: results => ({
            byId: Object.fromEntries(
                results
                    .filter(r => r.data)
                    .map(r => [r.data!.cryptoId, r.data!])
            ) as Record<string, CryptoBufferConfig>,
            isLoading: results.some(r => r.isLoading),
            isError: results.some(r => r.isError),
        }),
    })

const useInvalidate = () => {
    const queryClient = useQueryClient()
    return () => queryClient.invalidateQueries({ queryKey: [KEY] })
}

export const usePatchCryptoBuffer = () => {
    const invalidate = useInvalidate()
    return useMutation<
        void,
        unknown,
        { cryptoId: string; data: PatchCryptoBufferRequest }
    >({
        mutationFn: ({ cryptoId, data }) => patchCryptoBuffer(cryptoId, data),
        onSuccess: invalidate,
    })
}

export const useCreateBufferTier = () => {
    const invalidate = useInvalidate()
    return useMutation<
        void,
        unknown,
        { cryptoId: string; data: CreateBufferTierRequest }
    >({
        mutationFn: ({ cryptoId, data }) => createBufferTier(cryptoId, data),
        onSuccess: invalidate,
    })
}

export const useDeleteBufferTier = () => {
    const invalidate = useInvalidate()
    return useMutation<void, unknown, { cryptoId: string; tierId: string }>({
        mutationFn: ({ cryptoId, tierId }) =>
            deleteBufferTier(cryptoId, tierId),
        onSuccess: invalidate,
    })
}
